import type {
  ProtocolRangement,
  ProtocolRangementProblem,
  ProtocolStepDraft,
} from '../schemas/protocol.js';
import { variablesOf } from './variables.js';

/**
 * The pure rules of the « Rangement » section (scenario-succes.md §4, issue
 * #177), run by BOTH sides for the reason `protocolProblems` gives: the server
 * refuses the version, the editor draws the same refusal while the operator
 * types.
 *
 * Kept out of `problems.ts` because they read the section and the steps, where
 * the step rules read the steps alone, and because `schemas/protocol.ts` runs
 * `rangementTargetsKept` at the write boundary - a file that imported the
 * schemas AND was imported by them for a runtime value would be a cycle.
 */

/** What the rules read: the steps (for the objectives) and the section. */
export type RangementContent = {
  readonly steps: readonly ProtocolStepDraft[];
  readonly rangement: ProtocolRangement;
};

/**
 * The targets the steps still carry, in the order of the protocol, once each
 * (§4: « Un objectif cible retiré de la version sort de la liste à
 * l'enregistrement, sans refus »).
 *
 * The order is the protocol's and not the operator's ticking, because the
 * prompt lists the targets in that order and the table of Sessions follows it.
 * Run at the write boundary (`protocolContentWriteSchema`) and by the editor
 * on what it sends; idempotent.
 */
export function rangementTargetsKept(
  steps: readonly ProtocolStepDraft[],
  rangement: ProtocolRangement,
): ProtocolRangement {
  const asked = new Set(rangement.targetObjectiveIds);
  const kept: string[] = [];
  for (const step of steps) {
    for (const objective of step.objectives) {
      if (asked.has(objective.id) && !kept.includes(objective.id)) kept.push(objective.id);
    }
  }
  return { ...rangement, targetObjectiveIds: kept };
}

/**
 * What a version REFUSES in its Rangement section (§4, H6): targets ticked and
 * a consigne empty - spaces included, like the reading (N35) -, and a consigne
 * with no target to apply to.
 *
 * Judged on the targets the steps STILL carry (`rangementTargetsKept`): a ghost
 * target is dropped at the save, so a content whose only target is a ghost and
 * whose consigne is empty is the empty section it will be stored as, and is not
 * refused for it.
 */
export function rangementProblems(content: RangementContent): ProtocolRangementProblem[] {
  const { targetObjectiveIds, consigne } = rangementTargetsKept(content.steps, content.rangement);
  const hasConsigne = consigne.trim() !== '';
  if (targetObjectiveIds.length > 0 && !hasConsigne) {
    return [{ kind: 'rangement-consigne-missing' }];
  }
  if (targetObjectiveIds.length === 0 && hasConsigne) {
    return [{ kind: 'rangement-targets-missing' }];
  }
  return [];
}

/**
 * Does the consigne cite, with `{{objectif:id}}`, an objective the steps do not
 * carry? Then the rangeur reads « (objectif retiré) » where the name should be
 * (the régime of the reading, #158): a WARNING and never a refusal (§4).
 */
export function rangementConsigneOrphan(
  steps: readonly ProtocolStepDraft[],
  consigne: string,
): boolean {
  const objectiveIds = new Set(steps.flatMap((step) => step.objectives.map((o) => o.id)));
  return variablesOf(consigne).some(
    (variable) => variable.kind === 'objective' && !objectiveIds.has(variable.id),
  );
}
